import React, { createContext, useContext, useEffect, useState } from 'react'
import { OktaAuth } from '@okta/okta-auth-js'
import { oktaConfig, requiredGroups, superAdminGroups, adminGroups, ukGroups, dublinGroups, STUDIO_REGION_SCOPES, StudioRegionScope } from '../../config/auth'

interface User {
  sub: string
  name: string
  email: string
  preferred_username: string
  groups: string[]
}

interface AuthContextType {
  isAuthenticated: boolean
  user: User | null
  oktaAuth: OktaAuth
  login: () => Promise<void>
  logout: () => Promise<void>
  isLoading: boolean
  hasRequiredAccess: boolean
  isSuperAdmin: boolean
  isAdmin: boolean
  isPortalReadOnly: boolean
  isUK: boolean
  isDublin: boolean
  userGroups: string[]
  // null = no studio-region restriction (full visibility). Non-null = the user
  // belongs to a regional Okta group and is restricted to that region's home
  // studios (keywords + MT location ids).
  studioRegionScope: StudioRegionScope | null
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const oktaAuth = new OktaAuth(oktaConfig)

interface AuthProviderProps {
  children: React.ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [userGroups, setUserGroups] = useState<string[]>([])

  const checkAuthentication = async () => {
    try {
      console.log('Starting authentication check...')
      const authenticated = await oktaAuth.isAuthenticated()
      console.log('Authentication status:', authenticated)
      setIsAuthenticated(authenticated)

      if (authenticated) {
        const userInfo = await oktaAuth.getUser()
        
        // Extract groups from the access token or user info
        const rawGroups = userInfo.groups || []
        const groups = Array.isArray(rawGroups) 
          ? rawGroups.map(group => typeof group === 'string' ? group : String(group))
          : []
        
        setUser({
          sub: userInfo.sub || '',
          name: userInfo.name || userInfo.preferred_username || '',
          email: userInfo.email || '',
          preferred_username: userInfo.preferred_username || '',
          groups: groups
        })
        setUserGroups(groups)
      } else {
        setUser(null)
        setUserGroups([])
      }
    } catch (error) {
      console.error('Authentication check failed:', error)
      console.error('Error details:', error)
      setIsAuthenticated(false)
      setUser(null)
      setUserGroups([])
    } finally {
      console.log('Authentication check completed, setting loading to false')
      setIsLoading(false)
    }
  }

  useEffect(() => {
    // Initial authentication check
    checkAuthentication()

    // Listen for authentication state changes
    const handleAuthStateChange = (authState: any) => {
      console.log('Auth state change detected:', authState?.isAuthenticated)
      // Re-check authentication when state changes
      checkAuthentication()
    }

    oktaAuth.authStateManager.subscribe(handleAuthStateChange)

    return () => {
      oktaAuth.authStateManager.unsubscribe(handleAuthStateChange)
    }
  }, []) // Empty dependency array - only run on mount

  const login = async () => {
    try {
      console.log('Login button clicked, attempting Okta redirect...')
      console.log('Okta config:', {
        clientId: oktaAuth.options.clientId,
        issuer: oktaAuth.options.issuer,
        redirectUri: oktaAuth.options.redirectUri
      })
      await oktaAuth.signInWithRedirect()
      console.log('Redirect initiated successfully')
    } catch (error) {
      console.error('Login failed:', error)
      console.error('Error details:', error)
    }
  }

  const logout = async () => {
    try {
      await oktaAuth.signOut()
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  // Check if user is a super admin
  const isSuperAdmin = userGroups.some(group =>
    superAdminGroups.includes(group)
  )

  // Check if user is an admin (super admin or admin portal admin)
  const isAdmin = userGroups.some(group =>
    adminGroups.includes(group)
  )

  // Legacy flag identifies the User-only role. Its approved customer actions
  // are enforced per capability; membership in a higher role takes precedence.
  const isPortalReadOnly = userGroups.includes("Barry's-Admin-Portal-Users") &&
    !isAdmin &&
    !isSuperAdmin

  const hasRegionalScope = STUDIO_REGION_SCOPES.some((scope) =>
    userGroups.some((group) => (scope.groups as readonly string[]).includes(group))
  )

  // Regional customer scope is not yet enforced by every server route. Keep
  // regional-only users out of the portal, but preserve an explicit Portal
  // User membership because the server limits that tier to three safe reads.
  const hasRequiredAccess = userGroups.some(group =>
    requiredGroups.includes(group)
  ) && (!hasRegionalScope || isAdmin || isPortalReadOnly)

  // Check if user is a UK employee
  const isUK = userGroups.some(group =>
    ukGroups.includes(group)
  )

  // Check if user is a Dublin employee
  const isDublin = userGroups.some(group =>
    dublinGroups.includes(group)
  )

  // Resolve the studio-region scope (if any). Super admins bypass - they see
  // everyone regardless of group. First-match wins if a user is somehow in
  // multiple region groups.
  const studioRegionScope: StudioRegionScope | null = isSuperAdmin
    ? null
    : (STUDIO_REGION_SCOPES.find((s) => userGroups.some((g) => s.groups.includes(g))) ?? null)

  const value: AuthContextType = {
    isAuthenticated,
    user,
    oktaAuth,
    login,
    logout,
    isLoading,
    hasRequiredAccess,
    isSuperAdmin,
    isAdmin,
    isPortalReadOnly,
    isUK,
    isDublin,
    userGroups,
    studioRegionScope,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
